import { useState } from 'react';
import type { FlowRoute, StepViewProps } from './flow-route-types.js';
import { CheckboxGroup } from './choice-group.jsx';
import { dayLabels, fulfillmentMethodLabels, paymentMethodLabels } from './format.js';
import { TextField } from './form-field.jsx';
import {
  sectionErrors,
  type FulfillmentMethodDraft,
  type PaymentMethodDraft,
  type ScheduleDraft,
  type OnboardingDraft,
} from './onboarding-state.js';
import { useOnboarding } from './use-onboarding.jsx';

const questions = ['schedules', 'paymentMethods', 'fulfillmentMethods'] as const;
const timeOptions = buildTimeOptions();

export function StepService({ route, steps, showErrors, onNavigate }: StepViewProps) {
  const { draft, patchDraft } = useOnboarding();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [showInternalErrors, setShowInternalErrors] = useState(false);
  const errors = errorsFor(draft, showErrors || showInternalErrors);
  const service = draft.service;
  const question = questions[currentQuestion];

  function updateSchedule(day: string, patch: Partial<ScheduleDraft>) {
    patchDraft('service', {
      schedules: service.schedules.map((schedule) => {
        if (schedule.day !== day) {
          return schedule;
        }

        return { ...schedule, ...patch };
      }),
    });
  }

  function togglePayment(value: string, checked: boolean) {
    const method = value as PaymentMethodDraft;

    if (checked) {
      patchDraft('service', { paymentMethods: [...service.paymentMethods, method] });

      return;
    }

    patchDraft('service', {
      paymentMethods: service.paymentMethods.filter((item) => item !== method),
    });
  }

  function toggleFulfillment(value: string, checked: boolean) {
    const method = value as FulfillmentMethodDraft;

    if (checked) {
      patchDraft('service', { fulfillmentMethods: [...service.fulfillmentMethods, method] });

      return;
    }

    patchDraft('service', {
      fulfillmentMethods: service.fulfillmentMethods.filter((item) => item !== method),
    });
  }

  function updateDeliveryNotes(value: string) {
    patchDraft('service', { deliveryNotes: value });
  }

  function updateDeliveryAreaEnabled(enabled: boolean) {
    patchDraft('service', {
      deliveryArea: {
        ...service.deliveryArea,
        enabled,
      },
    });
  }

  function updateDeliveryRadius(value: string) {
    patchDraft('service', {
      deliveryArea: {
        ...service.deliveryArea,
        radiusKm: parseOptionalPositiveNumber(value),
      },
    });
  }

  function updateDeliveryNeighborhoods(value: string) {
    patchDraft('service', {
      deliveryArea: {
        ...service.deliveryArea,
        neighborhoods: parseNeighborhoods(value),
      },
    });
  }

  function continueFromQuestion() {
    if (!isCurrentQuestionValid(draft, question)) {
      setShowInternalErrors(true);
      focusFirstInvalid();
      return;
    }

    setShowInternalErrors(false);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((current) => current + 1);
      return;
    }

    const next = nextRoute(route, steps);

    if (next) {
      onNavigate(next.path);
    }
  }

  function backFromQuestion() {
    if (currentQuestion > 0) {
      setShowInternalErrors(false);
      setCurrentQuestion((current) => current - 1);
      return;
    }

    const previous = previousRoute(route, steps);

    if (previous) {
      onNavigate(previous.path);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <QuestionProgress current={currentQuestion + 1} total={questions.length} />

      <QuestionBody
        question={question}
        service={service}
        errors={errors}
        onScheduleChange={updateSchedule}
        onPaymentToggle={togglePayment}
        onFulfillmentToggle={toggleFulfillment}
        onDeliveryNotesChange={updateDeliveryNotes}
        onDeliveryAreaEnabledChange={updateDeliveryAreaEnabled}
        onDeliveryRadiusChange={updateDeliveryRadius}
        onDeliveryNeighborhoodsChange={updateDeliveryNeighborhoods}
      />

      <div className="flex flex-col-reverse items-stretch gap-3 border-t border-base-300 pt-6 sm:flex-row sm:items-center sm:justify-between">
        <button className="btn btn-ghost sm:w-auto" type="button" onClick={backFromQuestion}>
          Voltar
        </button>
        <button className="btn btn-primary sm:w-auto" type="button" onClick={continueFromQuestion}>
          {continueLabel(currentQuestion)}
        </button>
      </div>
    </div>
  );
}

function QuestionProgress({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex flex-col gap-2" aria-label={`Pergunta ${current} de ${total}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-primary">
        Pergunta {current} de {total}
      </p>
      <progress className="progress progress-primary h-2" value={current} max={total} />
    </div>
  );
}

function QuestionBody({
  question,
  service,
  errors,
  onScheduleChange,
  onPaymentToggle,
  onFulfillmentToggle,
  onDeliveryNotesChange,
  onDeliveryAreaEnabledChange,
  onDeliveryRadiusChange,
  onDeliveryNeighborhoodsChange,
}: {
  question: (typeof questions)[number];
  service: OnboardingDraft['service'];
  errors: Record<string, string>;
  onScheduleChange: (day: string, patch: Partial<ScheduleDraft>) => void;
  onPaymentToggle: (value: string, checked: boolean) => void;
  onFulfillmentToggle: (value: string, checked: boolean) => void;
  onDeliveryNotesChange: (value: string) => void;
  onDeliveryAreaEnabledChange: (enabled: boolean) => void;
  onDeliveryRadiusChange: (value: string) => void;
  onDeliveryNeighborhoodsChange: (value: string) => void;
}) {
  if (question === 'schedules') {
    return (
      <HorariosGrid
        schedules={service.schedules}
        error={errors.schedules}
        onChange={onScheduleChange}
      />
    );
  }

  if (question === 'paymentMethods') {
    return (
      <CheckboxGroup
        legend="Formas de pagamento aceitas"
        name="paymentMethods"
        values={service.paymentMethods}
        error={errors.paymentMethods}
        onToggle={onPaymentToggle}
        options={Object.entries(paymentMethodLabels).map(([value, label]) => ({ value, label }))}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <CheckboxGroup
        legend="Preferências de entrega e retirada"
        name="fulfillmentMethods"
        values={service.fulfillmentMethods}
        error={errors.fulfillmentMethods}
        hint="Escolha como o cliente pode receber o pedido."
        onToggle={onFulfillmentToggle}
        options={Object.entries(fulfillmentMethodLabels).map(([value, label]) => ({ value, label }))}
      />

      <label className="flex items-center gap-2 rounded-field border border-base-300 p-3 text-sm">
        <input
          type="checkbox"
          className="checkbox checkbox-primary checkbox-sm"
          checked={service.deliveryArea.enabled}
          onChange={(event) => onDeliveryAreaEnabledChange(event.target.checked)}
        />
        <span>Validar se o endereço está na área atendida</span>
      </label>

      <TextField
        id="deliveryRadiusKm"
        label="Raio máximo de entrega (km)"
        value={formatOptionalNumber(service.deliveryArea.radiusKm)}
        error={errors.deliveryArea}
        placeholder="Ex.: 4"
        inputMode="decimal"
        onChange={onDeliveryRadiusChange}
      />

      <TextField
        id="deliveryNeighborhoods"
        label="Bairros atendidos"
        value={service.deliveryArea.neighborhoods.join(', ')}
        hint="Opcional. Separe por vírgula. A validação considera a mesma cidade do endereço da loja."
        placeholder="Ex.: Copacabana, Botafogo, Ipanema"
        maxLength={240}
        onChange={onDeliveryNeighborhoodsChange}
      />

      <TextField
        id="deliveryNotes"
        label="Observações de entrega"
        value={service.deliveryNotes}
        error={errors.deliveryNotes}
        placeholder="Ex.: taxa por bairro, retirada no caixa 2"
        maxLength={240}
        onChange={onDeliveryNotesChange}
      />
    </div>
  );
}

function HorariosGrid({
  schedules,
  error,
  onChange,
}: {
  schedules: ScheduleDraft[];
  error?: string;
  onChange: (day: string, patch: Partial<ScheduleDraft>) => void;
}) {
  return (
    <fieldset
      className="fieldset w-full"
      aria-invalid={error ? true : undefined}
      tabIndex={error ? -1 : undefined}
    >
      <legend className="fieldset-legend">Horários de atendimento</legend>
      <div className="flex flex-col gap-2">
        {schedules.map((schedule) => (
          <div
            key={schedule.day}
            className="grid grid-cols-2 items-center gap-2 rounded-field border border-base-300 p-2 sm:grid-cols-[1fr_auto_auto]"
          >
            <label className="col-span-2 flex items-center gap-2 text-sm sm:col-span-1">
              <input
                type="checkbox"
                className="checkbox checkbox-primary checkbox-sm"
                checked={schedule.isOpen}
                onChange={(event) => onChange(schedule.day, { isOpen: event.target.checked })}
              />
              <span>{dayLabels[schedule.day]}</span>
            </label>
            <TimeSelect
              value={schedule.opensAt}
              disabled={!schedule.isOpen}
              label={`Abre ${dayLabels[schedule.day]}`}
              onChange={(value) => onChange(schedule.day, { opensAt: value })}
            />
            <TimeSelect
              value={schedule.closesAt}
              disabled={!schedule.isOpen}
              label={`Fecha ${dayLabels[schedule.day]}`}
              onChange={(value) => onChange(schedule.day, { closesAt: value })}
            />
          </div>
        ))}
      </div>
      <HorariosError error={error} />
    </fieldset>
  );
}

function TimeSelect({
  value,
  disabled,
  label,
  onChange,
}: {
  value: string;
  disabled: boolean;
  label: string;
  onChange: (value: string) => void;
}) {
  return (
    <select
      className="select select-sm w-full sm:w-28"
      value={value}
      disabled={disabled}
      aria-label={label}
      onChange={(event) => onChange(event.target.value)}
    >
      <TimeOption value={value} />
      {timeOptions.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

function TimeOption({ value }: { value: string }) {
  if (!value || timeOptions.includes(value)) {
    return null;
  }

  return <option value={value}>{value}</option>;
}

function HorariosError({ error }: { error?: string }) {
  if (!error) {
    return null;
  }

  return <p className="label text-error">{error}</p>;
}

function continueLabel(currentQuestion: number): string {
  if (currentQuestion === questions.length - 1) {
    return 'Próximo';
  }

  return 'Continuar';
}

function isCurrentQuestionValid(draft: OnboardingDraft, question: (typeof questions)[number]): boolean {
  const errors = sectionErrors(draft, 'service');

  if (question === 'schedules') {
    return !errors.schedules;
  }

  if (question === 'paymentMethods') {
    return !errors.paymentMethods;
  }

  return !errors.fulfillmentMethods && !errors.deliveryNotes && !errors.deliveryArea;
}

function parseOptionalPositiveNumber(value: string): number | undefined {
  const normalized = value.trim().replace(',', '.');

  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined;
  }

  return parsed;
}

function formatOptionalNumber(value: number | undefined): string {
  if (value === undefined) {
    return '';
  }

  return String(value);
}

function parseNeighborhoods(value: string): string[] {
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function errorsFor(draft: Parameters<typeof sectionErrors>[0], showErrors: boolean) {
  if (!showErrors) {
    return {};
  }

  return sectionErrors(draft, 'service');
}

function nextRoute(route: FlowRoute, steps: readonly FlowRoute[]): FlowRoute | undefined {
  const index = steps.findIndex((step) => step.path === route.path);
  return steps[index + 1];
}

function previousRoute(route: FlowRoute, steps: readonly FlowRoute[]): FlowRoute | undefined {
  const index = steps.findIndex((step) => step.path === route.path);
  return steps[index - 1];
}

function focusFirstInvalid() {
  window.setTimeout(() => {
    const invalid = document.querySelector<HTMLElement>('[aria-invalid="true"]');

    if (!invalid) {
      return;
    }

    invalid.focus();
  });
}

function buildTimeOptions(): string[] {
  return Array.from({ length: 48 }, (_unused, index) => {
    const hour = Math.floor(index / 2);
    let minute = '30';

    if (index % 2 === 0) {
      minute = '00';
    }

    return `${String(hour).padStart(2, '0')}:${minute}`;
  });
}
